import { c as _c } from "react/compiler-runtime";
import { GetStaticPropsContext, InferGetStaticPropsType } from "next";
import { fetchRouterContext } from "../../context/RouterContext";
import { ArticlePage, getArticlePaths } from "../ArticlePage";
import { GenericPage, getGenericPagePaths } from "../GenericPage";
import { ArticlesPage } from "../ArticlesPage";
import { VippsAgreementPage } from "../VippsAgreementPage";
import dynamic from "next/dynamic";
import { useEffect } from "react";
import { FullPageSpinner } from "../../components/shared/layout/FullPageSpinner/FullPageSpinner";
import { getDonationsPageSubpaths, getTaxPageSubPaths } from "../dashboard/paths";
// Only referenced from getStaticProps, so Next strips these imports from the client bundle
import { DonationsPage as DonationsPageData } from "../dashboard/DonationsPage";
import { AgreementsPage as AgreementsPageData } from "../dashboard/AgreementsPage";
import { ProfilePage as ProfilePageData } from "../dashboard/ProfilePage";
import { TaxPage as TaxPageData } from "../dashboard/TaxPage";
import { VippsAnonymousPage as VippsAnonymousPageData } from "../dashboard/VippsAnonymousPage";
import { ResultsPage } from "../ResultsPage";
import { LiveQuery } from "../../components/shared/LiveQuery";
import { ConsentState } from "../../proxy.page";
import { FundraiserPage, getFundraiserPagePaths } from "../FundraiserPage";
import { renderLatexInData } from "../../components/main/blocks/Paragraph/renderLatex";
enum PageType {
  GenericPage = "generic",
  ArticlesPage = "articles",
  ResultsPage = "results",
  ArticlePage = "article",
  FundraiserPage = "fundraiser",
  VippsAgreementPage = "vipps-agreement",
  VippsAnonymousPage = "vipps-anonymous",
  AgreementsPage = "agreements",
  DonationsPage = "donations",
  ProfilePage = "profile",
  TaxPage = "tax",
}

// Min side pages are split out so ordinary content pages don't load them (or Auth0). They are
// still server-rendered, and Next loads the chunks of rendered ones before hydrating. The
// placeholder matches what the profile layout shows while it checks the login.
const dashboardPageLoading = () => <FullPageSpinner />;
const DonationsPage = dynamic(() => import("../dashboard/DonationsPage").then(m => m.DonationsPage), {
  loading: dashboardPageLoading
});
const AgreementsPage = dynamic(() => import("../dashboard/AgreementsPage").then(m => m.AgreementsPage), {
  loading: dashboardPageLoading
});
const ProfilePage = dynamic(() => import("../dashboard/ProfilePage").then(m => m.ProfilePage), {
  loading: dashboardPageLoading
});
const TaxPage = dynamic(() => import("../dashboard/TaxPage").then(m => m.TaxPage), {
  loading: dashboardPageLoading
});
const VippsAnonymousPage = dynamic(() => import("../dashboard/VippsAnonymousPage").then(m => m.VippsAnonymousPage), {
  loading: dashboardPageLoading
});

// Min side pages only render once the login check passes, so start loading their code early
const preloadDashboardPage: Partial<Record<PageType, () => Promise<unknown>>> = {
  [PageType.DonationsPage]: () => import("../dashboard/DonationsPage"),
  [PageType.AgreementsPage]: () => import("../dashboard/AgreementsPage"),
  [PageType.ProfilePage]: () => import("../dashboard/ProfilePage"),
  [PageType.TaxPage]: () => import("../dashboard/TaxPage"),
  [PageType.VippsAnonymousPage]: () => import("../dashboard/VippsAnonymousPage")
};
const Page: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = props => {
  const $ = _c(35);
  let t0;
  let t1;
  if ($[0] !== props.pageType) {
    t0 = () => {
      preloadDashboardPage[props.pageType as PageType]?.();
    };
    t1 = [props.pageType];
    $[0] = props.pageType;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  if (props.draftMode) {
    switch (props.pageType as PageType) {
      case PageType.GenericPage:
        {
          const t2 = props as any;
          let t3;
          if ($[3] !== t2) {
            t3 = <PreviewGenericPage {...t2} />;
            $[3] = t2;
            $[4] = t3;
          } else {
            t3 = $[4];
          }
          return t3;
        }
      case PageType.ArticlesPage:
        {
          const t2 = props as any;
          let t3;
          if ($[5] !== t2) {
            t3 = <PreviewArticlesPage {...t2} />;
            $[5] = t2;
            $[6] = t3;
          } else {
            t3 = $[6];
          }
          return t3;
        }
      case PageType.ArticlePage:
        {
          const t2 = props as any;
          let t3;
          if ($[7] !== t2) {
            t3 = <PreviewArticlePage {...t2} />;
            $[7] = t2;
            $[8] = t3;
          } else {
            t3 = $[8];
          }
          return t3;
        }
      case PageType.ResultsPage:
        {
          const t2 = props as any;
          let t3;
          if ($[9] !== t2) {
            t3 = <PreviewResultsPage {...t2} />;
            $[9] = t2;
            $[10] = t3;
          } else {
            t3 = $[10];
          }
          return t3;
        }
      case PageType.FundraiserPage:
        {
          const t2 = props as any;
          let t3;
          if ($[11] !== t2) {
            t3 = <PreviewFundraiserPage {...t2} />;
            $[11] = t2;
            $[12] = t3;
          } else {
            t3 = $[12];
          }
          return t3;
        }
    }
  }
  switch (props.pageType as PageType) {
    case PageType.GenericPage:
      {
        const t2 = props as any;
        let t3;
        if ($[13] !== t2) {
          t3 = <GenericPage {...t2} />;
          $[13] = t2;
          $[14] = t3;
        } else {
          t3 = $[14];
        }
        return t3;
      }
    case PageType.ArticlesPage:
      {
        const t2 = props as any;
        let t3;
        if ($[15] !== t2) {
          t3 = <ArticlesPage {...t2} />;
          $[15] = t2;
          $[16] = t3;
        } else {
          t3 = $[16];
        }
        return t3;
      }
    case PageType.ArticlePage:
      {
        const t2 = props as any;
        let t3;
        if ($[17] !== t2) {
          t3 = <ArticlePage {...t2} />;
          $[17] = t2;
          $[18] = t3;
        } else {
          t3 = $[18];
        }
        return t3;
      }
    case PageType.ResultsPage:
      {
        const t2 = props as any;
        let t3;
        if ($[19] !== t2) {
          t3 = <ResultsPage {...t2} />;
          $[19] = t2;
          $[20] = t3;
        } else {
          t3 = $[20];
        }
        return t3;
      }
    case PageType.FundraiserPage:
      {
        const t2 = props as any;
        let t3;
        if ($[21] !== t2) {
          t3 = <FundraiserPage {...t2} />;
          $[21] = t2;
          $[22] = t3;
        } else {
          t3 = $[22];
        }
        return t3;
      }
    case PageType.VippsAgreementPage:
      {
        const t2 = props as any;
        let t3;
        if ($[23] !== t2) {
          t3 = <VippsAgreementPage {...t2} />;
          $[23] = t2;
          $[24] = t3;
        } else {
          t3 = $[24];
        }
        return t3;
      }
    case PageType.AgreementsPage:
      {
        const t2 = props as any;
        let t3;
        if ($[25] !== t2) {
          t3 = <AgreementsPage {...t2} />;
          $[25] = t2;
          $[26] = t3;
        } else {
          t3 = $[26];
        }
        return t3;
      }
    case PageType.VippsAnonymousPage:
      {
        const t2 = props as any;
        let t3;
        if ($[27] !== t2) {
          t3 = <VippsAnonymousPage {...t2} />;
          $[27] = t2;
          $[28] = t3;
        } else {
          t3 = $[28];
        }
        return t3;
      }
    case PageType.DonationsPage:
      {
        const t2 = props as any;
        let t3;
        if ($[29] !== t2) {
          t3 = <DonationsPage {...t2} />;
          $[29] = t2;
          $[30] = t3;
        } else {
          t3 = $[30];
        }
        return t3;
      }
    case PageType.ProfilePage:
      {
        const t2 = props as any;
        let t3;
        if ($[31] !== t2) {
          t3 = <ProfilePage {...t2} />;
          $[31] = t2;
          $[32] = t3;
        } else {
          t3 = $[32];
        }
        return t3;
      }
    case PageType.TaxPage:
      {
        const t2 = props as any;
        let t3;
        if ($[33] !== t2) {
          t3 = <TaxPage {...t2} />;
          $[33] = t2;
          $[34] = t3;
        } else {
          t3 = $[34];
        }
        return t3;
      }
  }
};
const PreviewGenericPage: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = props => {
  const $ = _c(5);
  let t0;
  if ($[0] !== props) {
    t0 = data => <GenericPage {...props as any} data={data} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.data || $[3] !== t0) {
    t1 = <LiveQuery data={props.data}>{t0}</LiveQuery>;
    $[2] = props.data;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const PreviewArticlesPage: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = props => {
  const $ = _c(5);
  let t0;
  if ($[0] !== props) {
    t0 = data => <ArticlesPage {...props as any} data={data} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.data || $[3] !== t0) {
    t1 = <LiveQuery data={props.data}>{t0}</LiveQuery>;
    $[2] = props.data;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const PreviewArticlePage: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = props => {
  const $ = _c(5);
  let t0;
  if ($[0] !== props) {
    t0 = data => <ArticlePage {...props as any} data={data} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.data || $[3] !== t0) {
    t1 = <LiveQuery data={props.data}>{t0}</LiveQuery>;
    $[2] = props.data;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const PreviewResultsPage: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = props => {
  const $ = _c(5);
  let t0;
  if ($[0] !== props) {
    t0 = data => <ResultsPage {...props as any} data={data} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.data || $[3] !== t0) {
    t1 = <LiveQuery data={props.data}>{t0}</LiveQuery>;
    $[2] = props.data;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const PreviewFundraiserPage: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = props => {
  const $ = _c(5);
  let t0;
  if ($[0] !== props) {
    t0 = data => <FundraiserPage {...props as any} data={data} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.data || $[3] !== t0) {
    t1 = <LiveQuery data={props.data}>{t0}</LiveQuery>;
    $[2] = props.data;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
function compareArrays<T>(a: T[], b: T[]) {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}
const inferPageTypeFromPath = async (path: string[]) => {
  const {
    articlesPagePath,
    fundraisersPath,
    resultsPagePath,
    vippsAgreementPagePath,
    dashboardPath,
    agreementsPagePath,
    taxPagePath,
    profilePagePath,
    vippsAnonymousPagePath
  } = await fetchRouterContext();
  const isVippsAgreementPage = vippsAgreementPagePath && compareArrays(path.slice(0, vippsAgreementPagePath.length), vippsAgreementPagePath);
  if (isVippsAgreementPage) return PageType.VippsAgreementPage;
  const isArticle = compareArrays(path.slice(0, articlesPagePath.length), articlesPagePath) && path.length > articlesPagePath.length;
  if (isArticle) return PageType.ArticlePage;
  const isFundraiser = compareArrays(path.slice(0, fundraisersPath.length), fundraisersPath) && path.length > fundraisersPath.length;
  if (isFundraiser) return PageType.FundraiserPage;
  const isArticlesPage = compareArrays(path.slice(0, articlesPagePath.length), articlesPagePath);
  if (isArticlesPage) return PageType.ArticlesPage;
  const isResultsPage = compareArrays(path.slice(0, resultsPagePath.length), resultsPagePath);
  if (isResultsPage) return PageType.ResultsPage;
  const isDashboard = dashboardPath ? compareArrays(path.slice(0, dashboardPath.length), dashboardPath) : false;
  if (isDashboard) {
    const isAgreementsPage = agreementsPagePath ? compareArrays(path.slice(0, agreementsPagePath.length), agreementsPagePath) : false;
    if (isAgreementsPage) return PageType.AgreementsPage;
    const isVippsAnonymousPage = vippsAnonymousPagePath ? compareArrays(path.slice(0, vippsAnonymousPagePath.length), vippsAnonymousPagePath) : false;
    if (isVippsAnonymousPage) return PageType.VippsAnonymousPage;
    const isTaxPage = taxPagePath ? compareArrays(path.slice(0, taxPagePath.length), taxPagePath) : false;
    if (isTaxPage) return PageType.TaxPage;
    const isProfilePage = profilePagePath ? compareArrays(path.slice(0, profilePagePath.length), profilePagePath) : false;
    if (isProfilePage) return PageType.ProfilePage;
    return PageType.DonationsPage;
  }
  return PageType.GenericPage;
};
export const getStaticProps = async (ctx: GetStaticPropsContext<{
  slug: string[];
  state: ConsentState;
}>) => {
  const result = await getPageStaticProps(ctx);
  // Render LaTeX on the server, so neither katex nor stale Studio-rendered HTML reaches the client
  return (result && "props" in result ? {
    ...result,
    props: renderLatexInData(result.props)
  } : result) as typeof result;
};
const getPageStaticProps = async (ctx: GetStaticPropsContext<{
  slug: string[];
  state: ConsentState;
}>) => {
  const path = ctx.params?.slug ?? [];
  const draftMode = ctx.draftMode ?? false;
  const consentState = ctx.params?.state ?? "undecided";
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? "";
  const isDanishSite = siteUrl.includes("giveffektivt.dk");
  const revalidate = isDanishSite || draftMode ? 1 : 3600;
  const pageType = await inferPageTypeFromPath(path);
  switch (pageType) {
    case PageType.GenericPage:
      {
        const props = await GenericPage.getStaticProps({
          draftMode,
          consentState,
          path
        });
        if (!props.data.result.page && !draftMode) {
          return {
            notFound: true
          };
        }
        return {
          props: {
            ...props,
            pageType,
            consentState
          },
          revalidate
        } as const;
      }
    case PageType.ArticlesPage:
      {
        const props = await ArticlesPage.getStaticProps({
          draftMode,
          consentState
        });
        return {
          props: {
            ...props,
            pageType,
            consentState
          },
          revalidate
        } as const;
      }
    case PageType.ArticlePage:
      {
        const slug = path.slice(1).join("/");
        const props = await ArticlePage.getStaticProps({
          draftMode,
          consentState,
          slug
        });
        return {
          props: {
            ...props,
            pageType,
            consentState
          },
          revalidate
        } as const;
      }
    case PageType.FundraiserPage:
      {
        const slug = path.slice(1).join("/");
        const props = await FundraiserPage.getStaticProps({
          draftMode,
          consentState,
          slug
        });
        if (!props.data.result.page && !draftMode) {
          return {
            notFound: true
          };
        }
        return {
          props: {
            ...props,
            pageType,
            consentState
          },
          revalidate
        } as const;
      }
    case PageType.ResultsPage:
      {
        const props = await ResultsPage.getStaticProps({
          draftMode,
          consentState
        });
        return {
          props: {
            ...props,
            pageType,
            consentState
          },
          revalidate
        } as const;
      }
    case PageType.VippsAgreementPage:
      {
        const props = await VippsAgreementPage.getStaticProps({
          draftMode,
          consentState
        });
        return {
          props: {
            ...props,
            pageType,
            consentState
          },
          revalidate
        } as const;
      }
    case PageType.VippsAnonymousPage:
      {
        const props = await VippsAnonymousPageData.getStaticProps({
          draftMode,
          consentState
        });
        return {
          props: {
            ...props,
            pageType,
            consentState
          },
          revalidate
        } as const;
      }
    case PageType.DonationsPage:
      {
        const props = await DonationsPageData.getStaticProps({
          draftMode,
          consentState,
          path
        });
        return {
          props: {
            ...props,
            pageType,
            consentState
          },
          revalidate
        } as const;
      }
    case PageType.AgreementsPage:
      {
        const props = await AgreementsPageData.getStaticProps({
          draftMode,
          consentState,
          slug: path
        });
        return {
          props: {
            ...props,
            pageType,
            consentState
          },
          revalidate
        } as const;
      }
    case PageType.ProfilePage:
      {
        const props = await ProfilePageData.getStaticProps({
          draftMode,
          consentState
        });
        return {
          props: {
            ...props,
            pageType,
            consentState
          },
          revalidate
        } as const;
      }
    case PageType.TaxPage:
      {
        const props = await TaxPageData.getStaticProps({
          draftMode,
          path,
          consentState
        });
        return {
          props: {
            ...props,
            pageType,
            consentState
          },
          revalidate
        } as const;
      }
  }
};
export async function getStaticPaths() {
  const routerContext = await fetchRouterContext();
  const basePaths = await Promise.all([getGenericPagePaths(), getArticlePaths(routerContext.articlesPagePath), getFundraiserPagePaths(routerContext.fundraisersPath), getDonationsPageSubpaths(), getTaxPageSubPaths()]).then(([genericPagePaths, articlePaths, donationsPageSubpaths, taxPageSubPaths]) => [...genericPagePaths, ...articlePaths, ...donationsPageSubpaths, ...taxPageSubPaths, ...(Object.values(routerContext).filter(path => path !== null) as string[][])]);
  const consentStates: ConsentState[] = ["accepted", "rejected", "undecided"];
  return {
    paths: basePaths.flatMap(path => consentStates.map(state => ({
      params: {
        // The frontpage slug "/" splits into ["", ""]; empty segments produce a
        // trailing-slash path ("/accepted/") that breaks Vercel's Next.js adapter
        slug: path.filter(segment => segment !== ""),
        state
      }
    }))),
    fallback: "blocking"
  };
}
export default Page;